import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import classNames from 'classnames';
import GalleryFavoriteList from '../GalleryFavoriteList';
import FolderListItem from 'components/matrices/FolderListItem';
import MatrixListItem from 'components/matrices/MatrixListItem';
import {
  fetchMatrix,
  saveMatrix,
  removeMatrix,
  convertExperimentToMatrix,
} from 'actions/matrices';
import { finishEdit } from 'actions/matrixSettings';
import { createFolder as createNewFolder } from 'constants/folders';
import {
  shouldFetchMatrix,
  canDelete,
  convertExperimentToMatrix as convertToMatrix,
} from 'constants/matrices';
import * as urls from 'constants/urls';
import ButtonNew from 'components/inputs/ButtonNew';
import Popover from 'components/Popover';
import FolderEditPopover from 'components/folders/FolderEditPopover';
import './MatrixList.scss';
import SimpleToggle from 'components/inputs/SimpleToggle';
import BulkDeleteModal from 'components/BulkDeleteModal';

const EducationModuleListItem = ({ setEducationTab }) => {
  const fakeMatrix = {
    imageUrl: 'images/education-modules.png',
    name: 'Education modules',
  };
  return (
    <div className="">
      <MatrixListItem
        draggable={false}
        matrix={fakeMatrix}
        onClick={setEducationTab}
        showCreatedBy={false}
        showModifiedDate={false}
      />
    </div>
  );
};

const MatrixSubList = ({
  experiments,
  matrix,
  dragEnd,
  dragStart,
  dragOver,
  enableReorder,
  onClick,
  folderId,
  setDeletePopoverOpen,
  deletePopoverOpen,
  setQuickSettingsPopoverOpen,
  quickSettingsPopoverOpen,
  addToBulkDelete,
  bulkDeleteMode,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <li
      id={matrix.id}
      className={classNames('MatrixSubList', { expanded })}
      draggable={enableReorder ? 'true' : 'false'}
      onDragOver={dragOver}
      onDragEnd={dragEnd}
      onDragStart={dragStart}
    >
      <MatrixListItem
        draggable={enableReorder}
        matrix={matrix}
        onClick={() => {
          onClick(matrix);
          setExpanded(true);
        }}
        onExpanderClicked={() => {
          setExpanded(!expanded);
        }}
        showCreatedBy={!matrix.isPublic && !matrix.isEducation}
        showModifiedDate={true}
        folderId={folderId}
        setDeletePopoverOpen={setDeletePopoverOpen}
        deletePopoverOpen={deletePopoverOpen === matrix.id}
        setQuickSettingsPopoverOpen={setQuickSettingsPopoverOpen}
        quickSettingsPopoverOpen={quickSettingsPopoverOpen === matrix.id}
        addToBulkDelete={addToBulkDelete}
        bulkDeleteMode={bulkDeleteMode}
      />
      {experiments.length ? (
        <ul className="MatrixSubList-experiments">
          {experiments.map(experiment => (
            <MatrixListItem
              key={experiment.id}
              matrix={experiment}
              onClick={() => onClick(experiment)}
              showCreatedBy={true}
              showImage={true}
              showModifiedDate={true}
              folderId={folderId}
              setDeletePopoverOpen={setDeletePopoverOpen}
              deletePopoverOpen={deletePopoverOpen === experiment.id}
              setQuickSettingsPopoverOpen={setQuickSettingsPopoverOpen}
              quickSettingsPopoverOpen={
                quickSettingsPopoverOpen === experiment.id
              }
              addToBulkDelete={addToBulkDelete}
              bulkDeleteMode={bulkDeleteMode}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

const MatrixList = ({
  experiments,
  matrices,
  enableReorder,
  folders,
  showFavorites,
  folderId,
  showEducation,
  setEducationTab,
  galleryTab,
}) => {
  // TODO temp set to true for dev
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [itemsToBulkDelete, setItemsToBulkDelete] = useState([]);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

  const [dragId, setDragId] = useState();
  const [matricesOrder, setMatrices] = useState();
  const [deletePopoverOpen, setDeletePopoverOpen] = useState(null);
  const [quickSettingsPopoverOpen, setQuickSettingsPopoverOpen] = useState(
    null
  );
  const newFolderRef = useRef(null);
  const currentFolderId = useSelector(state => state.folders.selected);
  const [pendingFolder, setPendingFolder] = useState(null);

  const visibleMatrices = useMemo(() => {
    return matrices.filter(m => !folders.map(f => f.id).includes(m?.folder));
  }, [matrices, folders]);

  const visibleExperiments = useMemo(() => {
    return experiments.filter(m => !folders.map(f => f.id).includes(m?.folder));
  }, [experiments, folders]);

  useEffect(() => setMatrices(visibleMatrices), [visibleMatrices]);
  const dispatch = useDispatch();

  const matricesLoading = useSelector(state => state.matrices._loading);
  const matricesLoaded = useSelector(state => state.matrices._loaded);

  const auth = useSelector(state => state.auth);
  const userId = useMemo(() => auth.user?.claims?.user_id ?? null, [auth.user]);

  const { user, userData } = auth;

  const favorites = useMemo(() => {
    return matrices
      .concat(experiments)
      .concat(folders.map(f => ({ ...f, isFolder: true })))
      .filter(item => item.favorited && item.favorited.includes(userId));
  }, [userId, matrices, experiments, folders]);

  const history = useHistory();
  const goToMatrix = matrix => history.push(urls.matrix(matrix));
  const goToExperiment = experiment =>
    history.push(urls.experiment(experiment));

  const onClick = item => {
    // Experiments have parentId
    if (item.parentId) {
      goToExperiment(item);
    } else {
      goToMatrix(item);
    }
  };

  const handleDragStart = e => {
    setDragId(e.currentTarget.id);
  };

  const handleDragEnd = () => {
    if (matricesOrder) {
      const dragMatrix = matricesOrder.find(d => d.id === dragId);
      const reorderMatrices = matricesOrder
        .filter(d => d.id !== dragId)
        .map((d, i) => ({
          ...(d.type === 'dragDivider' ? dragMatrix : d),
          order: i,
        }));
      setMatrices(reorderMatrices);

      reorderMatrices.forEach(matrix => {
        matrix.savedByUser = true;
        dispatch(finishEdit(matrix, false));
        dispatch(saveMatrix(matrix.id));
      });
    }
  };

  const handleDragOver = e => {
    e.preventDefault();
    if (e.currentTarget.id !== dragId) {
      const currDivider =
        matricesOrder && matricesOrder.findIndex(d => d.type === 'dragDivider');
      const reorderMatrices = (matricesOrder || visibleMatrices).filter(
        d => d.type !== 'dragDivider'
      );
      const dragOverId = e.currentTarget.id;
      const dragOverBounds = e.currentTarget.getBoundingClientRect();
      const isDragBefore =
        e.clientY < dragOverBounds.top + dragOverBounds.height / 2;
      const dragIndex =
        reorderMatrices.findIndex(d => d.id === dragOverId) +
        (isDragBefore ? 0 : 1);
      if (currDivider !== dragIndex) {
        reorderMatrices.splice(dragIndex, 0, { type: 'dragDivider' });
        setMatrices(reorderMatrices);
      }
    }
  };

  // System to only allow one popover open at a time
  const handleDeletePopover = id => {
    if (deletePopoverOpen === id) {
      setDeletePopoverOpen(null);
    } else {
      setDeletePopoverOpen(id);
      setQuickSettingsPopoverOpen(null);
    }
  };

  const handleQuickSettingsPopover = id => {
    if (quickSettingsPopoverOpen === id) {
      setQuickSettingsPopoverOpen(null);
    } else {
      setQuickSettingsPopoverOpen(id);
      setDeletePopoverOpen(null);
    }
  };

  const onCreateNewFolder = useCallback(() => {
    if (pendingFolder) {
      setPendingFolder(null);
    } else {
      const newFolder = createNewFolder(auth.user);
      setPendingFolder(newFolder);
    }
  }, [pendingFolder, auth]);

  const addToBulkDelete = useCallback(
    id => {
      let nextBulkDelete = [...itemsToBulkDelete];
      if (nextBulkDelete.includes(id)) {
        nextBulkDelete = nextBulkDelete.filter(v => v !== id);
      } else {
        // Fetch the matrix if we don't have it at this stage
        const matrix = visibleMatrices
          .concat(visibleExperiments)
          .find(m => m.id === id);
        if (!matrix) return;
        // Make sure we fetch matrices before deleting as
        // they are needed for that function to run
        if (shouldFetchMatrix(id, matrix, matricesLoaded, matricesLoading)) {
          dispatch(fetchMatrix(id));
        }
        const children = visibleExperiments.filter(e => e?.parentId === id);
        for (const child of children) {
          if (
            shouldFetchMatrix(child.id, child, matricesLoaded, matricesLoading)
          ) {
            dispatch(fetchMatrix(child.id));
          }
        }

        nextBulkDelete = nextBulkDelete.concat([id]);
      }
      setItemsToBulkDelete(nextBulkDelete);
    },
    [
      itemsToBulkDelete,
      dispatch,
      visibleMatrices,
      visibleExperiments,
      matricesLoaded,
      matricesLoading,
    ]
  );

  // If you go into or out of a folder, reset bulk delete mode
  useEffect(() => {
    if (currentFolderId) {
      setBulkDeleteMode(false);
    }
  }, [currentFolderId]);

  // If bulk delete mode is false, remove matrices to delete
  useEffect(() => {
    if (!bulkDeleteMode) {
      setItemsToBulkDelete([]);
    }
  }, [bulkDeleteMode]);

  const bulkDeleteMatrixNames = useMemo(() => {
    let names = visibleMatrices
      .concat(visibleExperiments)
      .filter(m => itemsToBulkDelete.includes(m.id));
    names = names.map(n => n.name);
    return names;
  }, [visibleExperiments, visibleMatrices, itemsToBulkDelete]);

  const onBulkDelete = useCallback(() => {
    for (const id of itemsToBulkDelete) {
      dispatch(removeMatrix(id));

      // If the matrix has experiment children, convert them to matrices
      const children = visibleExperiments.filter(e => e?.parentId === id);
      for (const child of children) {
        // If we're also deleting a child, don't make a new matrix from it
        if (itemsToBulkDelete.includes(child.id)) continue;

        const nextMatrix = convertToMatrix(child, user);
        dispatch(convertExperimentToMatrix(nextMatrix));
        dispatch(saveMatrix(nextMatrix.id));
      }
    }
    // Reset
    setBulkDeleteModalOpen(false);
    setBulkDeleteMode(false);
    setItemsToBulkDelete([]);
  }, [dispatch, user, itemsToBulkDelete, visibleExperiments]);

  return (
    <>
      {showFavorites ? (
        <GalleryFavoriteList favorites={favorites} onClick={onClick} />
      ) : null}
      <ul className="MatrixList" onDragOver={e => e.preventDefault()}>
        {showEducation ? (
          <EducationModuleListItem setEducationTab={setEducationTab} />
        ) : null}

        {auth.user && galleryTab.name === 'private' ? (
          <div
            className="MatrixList-new-folder-button-container"
            ref={newFolderRef}
          >
            {!currentFolderId ? (
              <ButtonNew
                buttonClasses={'text-button'}
                onClick={onCreateNewFolder}
                label="New folder"
                iconLeft="plus"
                requiresLogin
                requiresLoginMessage="Sign in to add a new folder."
              />
            ) : (
              <div />
            )}

            <div className="MatrixList-bulk-delete-toggle">
              <SimpleToggle
                value={bulkDeleteMode}
                label={'Select multiple'}
                onChange={setBulkDeleteMode}
              />
              <ButtonNew
                buttonClasses={classNames('icon-button', {
                  disabled: !bulkDeleteMode,
                })}
                icon={'trash'}
                onClick={() => setBulkDeleteModalOpen(true)}
              />
            </div>
          </div>
        ) : null}

        <div className="MatrixSubList">
          {folders.map(folder => (
            <FolderListItem
              key={folder.id}
              folder={folder}
              setDeletePopoverOpen={handleDeletePopover}
              deletePopoverOpen={deletePopoverOpen === folder.id}
              setQuickSettingsPopoverOpen={handleQuickSettingsPopover}
              quickSettingsPopoverOpen={quickSettingsPopoverOpen === folder.id}
              disableDelete={bulkDeleteMode}
            />
          ))}
        </div>
        {(matricesOrder || visibleMatrices).map(matrix =>
          matrix.type === 'dragDivider' ? (
            <div className="MatrixListDivider" key="dragDivider" />
          ) : (
            <MatrixSubList
              key={matrix.id}
              experiments={visibleExperiments.filter(
                ({ parentId }) => parentId === matrix.id
              )}
              matrix={matrix}
              dragEnd={handleDragEnd}
              dragStart={handleDragStart}
              dragOver={handleDragOver}
              enableReorder={enableReorder}
              onClick={onClick}
              folderId={folderId}
              setDeletePopoverOpen={handleDeletePopover}
              deletePopoverOpen={deletePopoverOpen}
              setQuickSettingsPopoverOpen={handleQuickSettingsPopover}
              quickSettingsPopoverOpen={quickSettingsPopoverOpen}
              addToBulkDelete={addToBulkDelete}
              bulkDeleteMode={bulkDeleteMode}
            />
          )
        )}
      </ul>
      <Popover
        title={`New folder`}
        content={
          <FolderEditPopover
            folder={pendingFolder}
            onClose={() => setPendingFolder(null)}
            isNew={true}
          />
        }
        isOpen={!!pendingFolder}
        parentRef={newFolderRef.current}
      />
      {bulkDeleteModalOpen ? (
        <BulkDeleteModal
          matrixNames={bulkDeleteMatrixNames}
          onCancel={() => setBulkDeleteModalOpen(false)}
          onExit={() => setBulkDeleteModalOpen(false)}
          onConfirm={onBulkDelete}
          disabled={itemsToBulkDelete.some(id => !matricesLoaded[id])}
        />
      ) : null}
    </>
  );
};

export default MatrixList;
